import "../../index.scss";
import "./index.scss";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  clearStats,
  setDifficulty,
} from "../../Store/store";
import { useLocation } from "react-router-dom";
import {
  fetchImage,
  getSearchParams,
  findGameFromConfigAndParams,
} from "../nonUIFuncs";
import { Language } from "../../config";
import { tutorialFor } from "../../data/tutorials";
import { forgetTutorial } from "../../utils/tutorialSeen";
import PropTypes from "prop-types";

/**
 * Container component for the play screen that displays game instructions and controls.
 * Provides layout structure with game description, action buttons, and game type badge.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components (typically difficulty buttons)
 * @param {string} props.gameDescription - Text description of the game
 * @param {string} props.gameTypeImage - Filename of the game type badge image
 * @returns {JSX.Element} Play screen container with game information layout
 */
const PlayScreenContainer = ({ children, gameDescription, gameTypeImage, onHowToPlay }) => {
  return (
    <div className="playscreen_container">
      <div className="playscreen_content">
        {/* {location.pathname.includes("/endless-mode/") ? (
          <div>
            {endlessCategories.map((d) => {
              return (
                <div className="playscreen_gametypebadgecontainer">
                  <div
                    className="playscreen_gametypebadge"
                    style={{
                      backgroundImage: `url(${fetchImage(gameTypeImage)})`,
                    }}
                  />
                </div>
              );
            })}
          </div>
        ) : null} */}

        <div className="playscreen_buttoncontainer">{children}</div>
        <div className="playscreen_descriptionbox">
          <p className="playscreen_descriptiontext">{gameDescription}</p>
          {onHowToPlay && (
            <button type="button" className="playscreen_howto badaBoom" onClick={onHowToPlay}>
              How to play
            </button>
          )}
        </div>
      </div>
      <div className="playscreen_gametypebadgecontainer">
        <div
          className="playscreen_gametypebadge"
          style={{ backgroundImage: `url(${fetchImage(gameTypeImage)})` }}
        />
      </div>
    </div>
  );
};

/**
 * Play screen component that displays game instructions and difficulty selection.
 * Shows difficulty buttons (Easy, Medium, Hard) with availability based on player progress.
 * Manages difficulty selection and transitions to the game screen.
 *
 * @param {Object} props - Component props
 * @param {Function} props.setScreen - Function to change the current screen
 * @param {string} props.gameDescription - Description text for the current game
 * @param {Array} props.scoreData - Array of player score data for determining unlocked difficulties
 * @param {string} props.gameTypeImage - Filename of the game type badge image
 * @param {Object} props.config - Application configuration object
 * @returns {JSX.Element} Play screen with difficulty selection and game description
 */
const PlayScreen = ({
  setScreen,
  gameDescription,
  scoreData,
  gameTypeImage,
  config,
}) => {
  const [mediumDisabled, setMediumDisabled] = useState(true);
  const [hardDisabled, setHardDisabled] = useState(true);
  const location = useLocation();
  const params = getSearchParams(location);
  const dispatch = useDispatch();
  useEffect(() => {
    if (scoreData.length > 0) {
      let difficultyParam = params.find((p) => p.id === "difficulty").value;
      if (!location.pathname.includes("/endless-mode/")) {
        let unitParam = params.find((p) => p.id === "unit").value;
        let gameNameParam = params.find((p) => p.id === "game-name").value;
        let game = scoreData.find(
          (d) => d.gameName === gameNameParam && d.category === unitParam,
        );
        // let filteredGames = [];
        // config.ParamsToFindGame.forEach((p) => {
        //   let arrayToFilter = filteredGames.length > 0 ? filteredGames : scoreData;
        //   let param = params.find((d) => d.id === p.param);
        //   let value = param.value;
        //   let dataToCheck = p.data;
        //   if (param.id === 'game-type') {
        //     value = value.replaceAll(' ', '').replaceAll('-', '');
        //     dataToCheck = 'gameType';
        //   }
        //   arrayToFilter = arrayToFilter.filter((d) => d[dataToCheck] === value);
        //   filteredGames = arrayToFilter;
        // });
        game = findGameFromConfigAndParams(scoreData, params);
        if (game !== undefined) {
          if (game?.difficultyScores.medium.difficulty <= difficultyParam) {
            setMediumDisabled(false);
          }
          if (game?.difficultyScores.hard.difficulty <= difficultyParam) {
            setHardDisabled(false);
          }
        }
      }
    }
  }, [scoreData]);
  let gameType = params.find((d) => d.id === "game-type").value;
  // if (gameType === 'Verb Activity') {
  //   return (
  //     <PlayScreenContainer
  //       gameDescription={gameDescription}
  //       gameTypeImage={gameTypeImage}>
  //       <div
  //         className="playscreen_playbutton hovercursor"
  //         onClick={() => {
  //           setScreen("gameScreen");
  //           dispatch(clearStats());
  //           dispatch(setDifficulty("easy"));
  //         }}>
  //         <h1 className="playscreen_playtext">Play!</h1>
  //       </div>
  //     </PlayScreenContainer>
  //   );
  // }
  if (gameType === "Story Book") {
    return (
      <PlayScreenContainer
        gameDescription="Read the Story!"
        // gameDescription={gameDescription}
        gameTypeImage={gameTypeImage}
      >
        <div
          className="playscreen_playbutton hovercursor"
          onClick={() => {
            setScreen("gameScreen");
            dispatch(clearStats());
            dispatch(setDifficulty("easy"));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setScreen("gameScreen");
              dispatch(clearStats());
              dispatch(setDifficulty("easy"));
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Play story book"
        >
          <h1 className="playscreen_playtext">Play!</h1>
        </div>
      </PlayScreenContainer>
    );
  }
  // "How to play": the first-play tutorial again, on Easy
  const howToPlay = tutorialFor(gameType)
    ? () => {
        forgetTutorial(gameType, config?.Language ?? Language);
        setScreen("gameScreen");
        dispatch(clearStats());
        dispatch(setDifficulty("easy"));
      }
    : undefined;
  return (
    <PlayScreenContainer
      gameDescription={gameDescription}
      gameTypeImage={gameTypeImage}
      onHowToPlay={howToPlay}
    >
      <div
        className="playscreen_playbutton hovercursor"
        onClick={() => {
          setScreen("gameScreen");
          dispatch(clearStats());
          dispatch(setDifficulty("easy"));
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setScreen("gameScreen");
            dispatch(clearStats());
            dispatch(setDifficulty("easy"));
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Easy difficulty"
      >
        <h1 className="playscreen_playtext">Easy!</h1>
      </div>
      <div
        className={`
        playscreen_playbutton
        ${mediumDisabled ? "_disabled" : "hovercursor"}
      `}
        onClick={() => {
          if (!mediumDisabled) {
            setScreen("gameScreen");
            dispatch(clearStats());
            dispatch(setDifficulty("medium"));
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (!mediumDisabled) {
              setScreen("gameScreen");
              dispatch(clearStats());
              dispatch(setDifficulty("medium"));
            }
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Medium difficulty"
        aria-disabled={mediumDisabled}
      >
        <h1 className="playscreen_playtext">Medium!</h1>
      </div>
      <div
        className={`
        playscreen_playbutton
        ${hardDisabled ? "_disabled" : "hovercursor"}
      `}
        onClick={() => {
          if (!hardDisabled) {
            setScreen("gameScreen");
            dispatch(clearStats());
            dispatch(setDifficulty("hard"));
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (!hardDisabled) {
              setScreen("gameScreen");
              dispatch(clearStats());
              dispatch(setDifficulty("hard"));
            }
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Hard difficulty"
        aria-disabled={hardDisabled}
      >
        <h1 className="playscreen_playtext">Hard!</h1>
      </div>
    </PlayScreenContainer>
  );
};

export default PlayScreen;

PlayScreen.propTypes = {
  setScreen: PropTypes.func,
  gameDescription: PropTypes.string,
  scoreData: PropTypes.array,
  gameTypeImage: PropTypes.string,
};
